import { tt } from "../../utils/i18n/i18n";
import imgMC from "./img/mastercard.png";
import imgVisa from "./img/visa.png";
import React, { useEffect, useState } from "react";
import PaymentFormAPI from "./api";
import KC_Span from "../../designsystem/KC_Span/KC_Span";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../designsystem/KC_Button/KC_Button";
import Separator from "../Separator";
import TestsID from "../../utils/TestsID";
import { DEFAULT_PLAN_OPTION } from "../../utils/constants";
import CGVCheckList from "../CGVChecklist/CGVCheckList";
import { SaasCommitment } from "../../model/SaasTypes";
import { useDispatch } from "react-redux";
import {
  setError,
  setLoading,
  useLoadingSelector
} from "../../redux/actions/app";
import PaymentCard from "../../model/PaymentCard";
import stripePromise from "../stripePromise";
import KC_UIText from "../../designsystem/typography/KC_UIText/KC_UIText";

interface Props {
  paymentCards: PaymentCard[] | null;
  businessUserId: string;
  posaDeliveryIds: string[];
  amount: number;
  goToInsertCard: () => void;
  afterPaymentSuccessful: (
    paymentIntentId: string | undefined | null,
    setupIntentId: string | undefined | null
  ) => Promise<void>;
  saasCommitment?: SaasCommitment | null;
  onPayStarted?: () => void;
  onPaymentError?: (error: Error | string) => void;
  formattedButtonText: string;
  buttonSubText?: string;
  isFirstMonthFree?: boolean;
  shouldUseSetupIntent?: boolean;
  customPaymentHandler?: (
    cardId: string
  ) => Promise<{ paymentIntentId: string }>;
}

export default function ChooseCardForm(props: Props) {
  const dispatch = useDispatch();
  const isLoading = useLoadingSelector();

  const [selectedCard, setSelectedCard] = useState("");

  const payWithCard = async (event: React.FormEvent) => {
    event.preventDefault();
    props?.onPayStarted?.();
    dispatch(setLoading(true));

    // For setup intent (first month free or future start date), verify card availability
    // by creating and confirming a setup intent with the saved payment method
    if (props.shouldUseSetupIntent) {
      try {
        const stripe = await stripePromise;

        if (!stripe) {
          dispatch(setError(tt("api_error")));
          dispatch(setLoading(false));
          return;
        }

        // Create a setup intent
        const setupIntentResponse =
          await PaymentFormAPI.createStripeSetupIntent(props.businessUserId);

        // Confirm the setup intent with the saved payment method
        const result = await stripe.confirmCardSetup(
          setupIntentResponse.clientSecret,
          {
            payment_method: selectedCard
          }
        );

        if (result.error) {
          dispatch(setError(result.error.message || tt("somethingWentWrong")));
          dispatch(setLoading(false));
          return;
        }

        // Card verified successfully, complete subscription without payment
        await props.afterPaymentSuccessful(
          "",
          setupIntentResponse.setupIntentId
        );
        return;
      } catch (error) {
        console.error("Error verifying card:", error);
        dispatch(setError(tt("somethingWentWrong")));
        dispatch(setLoading(false));
        return;
      }
    }

    try {
      let res;

      if (props.customPaymentHandler) {
        res = await props.customPaymentHandler(selectedCard);
      } else {
        res = await PaymentFormAPI.createStripePaymentIntentWithoutBill(
          props.businessUserId,
          props.amount,
          selectedCard
        );
      }

      await props.afterPaymentSuccessful(res.paymentIntentId, undefined);
    } catch (error: any) {
      console.error("Payment error:", error);
      dispatch(setLoading(false));

      if (props.onPaymentError) {
        props.onPaymentError(error);
      } else {
        dispatch(setError(error?.message || tt("api_error")));
      }
    }
  };

  useEffect(() => {
    if (props.paymentCards?.length) {
      setSelectedCard(props.paymentCards[0].id);
    }
  }, [props.paymentCards]);

  return (
    <form onSubmit={payWithCard}>
      {props.paymentCards?.map((card, idx) => {
        const isVisa = card.cardType === "visa";
        const isMasterCard = card.cardType === "mastercard";
        return (
          <div
            key={`card-${idx}`}
            className="kc-card kc-card-no-max-width w-100"
          >
            <div className="input-group-prepend w-100">
              <div
                className="input-group-text w-100 align-items-center"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="radio"
                  id={`card-${idx}`}
                  checked={selectedCard === card.id}
                  disabled={isLoading}
                  style={{ cursor: "pointer" }}
                  onChange={() => {
                    if (selectedCard !== card.id) {
                      setSelectedCard(card.id);
                    }
                  }}
                />
                <label
                  htmlFor={`card-${idx}`}
                  className="d-block d-flex flex-row align-items-center justify-content-between ml-3 mb-0 w-100"
                  style={{ cursor: "pointer" }}
                >
                  <KC_Span text={`${card.tokenizedCardNumber}`} />
                  <KC_Span
                    text={`${tt("card_expires")} ${card.expirationDate}`}
                  />
                  <div>
                    {isMasterCard && <img src={imgMC} style={{ height: 20 }} />}
                    {isVisa && <img src={imgVisa} style={{ height: 20 }} />}
                  </div>
                </label>
              </div>
            </div>
          </div>
        );
      })}

      {props.saasCommitment && (
        <CGVCheckList
          amountToPay={props.amount}
          showMonthlyPayment={props.saasCommitment !== DEFAULT_PLAN_OPTION}
        />
      )}

      <Separator />

      <div className="d-flex flex-column flex-sm-row align-items-center justify-content-end w-100 st-gap">
        {!isLoading && (
          <>
            <KC_Button
              size={KC_ButtonSize.Large}
              id={TestsID.SAAS_FLYERS_ADD_CARD_BUTTON}
              isPrimary={false}
              isTransparent={true}
              onClick={() => {
                props.goToInsertCard();
              }}
              className="bordered-black"
              text={tt("add_card")}
              extraStyle={{
                width: "unset !important"
              }}
            />
            <KC_Button
              size={KC_ButtonSize.Large}
              id={TestsID.checkoutButton}
              text={props.formattedButtonText}
              type={KC_ButtonType.Submit}
              isSecondary={true}
              disabled={!selectedCard}
              className="flex-grow-1"
            />
          </>
        )}
      </div>

      {!!props.buttonSubText && (
        <div className="d-flex flex-row align-items-center justify-content-end mt-4 w-100 st-gap">
          <KC_UIText
            text={props.buttonSubText}
            className="text-right"
            weight="reg"
            fontStyle="italic"
          />
        </div>
      )}
    </form>
  );
}
